/**
 * Routes auth failures to Sentry, deliberately NOT one-to-one with the event pipeline.
 *
 * The two systems answer different questions and conflating them ruins both:
 *   Sentry  -> engineers. "Is this a bug?" Wants stack traces and source maps. Low volume.
 *   Events  -> support and product. "Which customers are stuck, and why?" Wants an email, a
 *              flow and a code, queryable in SQL. Higher volume, no stack trace needed.
 *
 * Capturing every wrong password in Sentry would burn quota, create alert fatigue, and still
 * give support nothing they can query. So expected user errors become breadcrumbs — free, and
 * they provide the context if a real exception is captured later in the same session.
 *
 * Sentry is disabled in dev (main.ts gates on VITE_APP_STAGE), which is exactly why the event
 * reporter runs everywhere: locally it is the only channel that works.
 */

import * as Sentry from '@sentry/vue';
import { isMappedErrorCode } from '@/utils/error.utils';
import type { AuthErrorSource } from '@/types/authEvent.types';

interface SentryRouteInput {
  errorSource: AuthErrorSource;
  errorCode: string;
  stage: string;
  step: string;
  error?: unknown;
  httpStatus?: number | null;
}

/** True when this failure represents a bug rather than a user mistake. */
function isEngineeringProblem(input: SentryRouteInput): boolean {
  switch (input.errorSource) {
    // Our own code broke, or the browser refused something. Always a bug.
    case 'CLIENT':
    case 'BROWSER':
      return true;

    // 5xx and network faults are ours; 4xx is the user or their state.
    case 'AUTH_API':
      return input.httpStatus == null || input.httpStatus >= 500;

    // A Cognito code we have no translation for means we do not understand this failure yet.
    // Useful side effect: Sentry becomes the to-do list for ERROR_KEY_MAP gaps.
    case 'COGNITO':
    default:
      return !isMappedErrorCode(input.errorCode);
  }
}

export function routeToSentry(input: SentryRouteInput): void {
  try {
    const context = {
      'auth.stage': input.stage,
      'auth.step': input.step,
      'auth.errorCode': input.errorCode,
      'auth.errorSource': input.errorSource,
      ...(input.httpStatus != null ? { 'auth.httpStatus': input.httpStatus } : {}),
    };

    if (!isEngineeringProblem(input)) {
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: `${input.stage}: ${input.errorCode}`,
        data: context,
      });
      return;
    }

    Sentry.captureException(input.error ?? new Error(`${input.stage}: ${input.errorCode}`), {
      tags: {
        'auth.stage': input.stage,
        'auth.errorCode': input.errorCode,
        'auth.errorSource': input.errorSource,
      },
      extra: context,
    });
  } catch {
    // Telemetry must never be the thing that breaks authentication.
  }
}

/** Attaches the email to the Sentry scope so a captured exception is actionable. */
export function setSentryUser(email: string | null): void {
  try {
    Sentry.setUser(email ? { email } : null);
  } catch {
    /* ignore */
  }
}
